// Seed data for Invitation - Internal (Panitia Lelang).
//
// TODO(API): placeholder until the internal-invitation contract lands.
//
// Field names deliberately match what planningApi.js's normalizers produce and
// what the shared read-only CargoTable expects (cargoType / ot / volume /
// ddrFrom / ddrTo / ddrNotes / incoterms / incotermsNotes / plant / unit /
// crudes[].{crudeName,maxCapacity,remarkMaxCapacity,isViaCibOnly,bleeding1,
// bleeding2}) so a live endpoint returning the Planning shape drops straight in.
//
// `invitationDetail` is the data entered when creating the "Invitation to
// Panitia Lelang" (session day/time/place/agenda); `participant` is filled only
// after the member Confirms as a Delegate.

import { INVITATION_STATUS, DOC_SOURCE } from './invitationInternalConstants';

const cargo = (over = {}) => ({
  id: 'cargo-1',
  cargoType: 'Light',
  ot: 10,
  volume: 100,
  ddrFrom: '2026-09-01T00:00:00+00:00',
  ddrTo: '2026-09-10T00:00:00+00:00',
  ddrNotes: 'Laycan subject to terminal confirmation',
  incoterms: ['FOB'],
  incotermsNotes: 'FOB Dumai, load port cost on seller',
  plant: 'RU II Dumai',
  unit: 'CDU DMI',
  crudes: [
    {
      id: 'crude-1',
      crudeName: 'BREGA',
      maxCapacity: 50,
      remarkMaxCapacity: 'Limited by desalter capacity',
      isViaCibOnly: false,
      bleeding1: 10,
      bleeding2: 5,
    },
    {
      id: 'crude-2',
      crudeName: 'SENIPAH CONDENSATE',
      maxCapacity: 30,
      remarkMaxCapacity: '',
      isViaCibOnly: true,
      bleeding1: 8,
      bleeding2: null,
    },
  ],
  ...over,
});

const clauses = () => [
  {
    clauseId: 'clause-1',
    title: 'Price DAP',
    mandatory: true,
    summernote:
      'Price shall be based on the average of Platts quotations for the three days after the Bill of Lading date.',
  },
  {
    clauseId: 'clause-2',
    title: 'Quality & Quantity Determination',
    mandatory: false,
    summernote:
      'Quality and quantity as determined by an independent inspector at the load port shall be final and binding.',
  },
];

// Internal sees documents from both stages; no showToDmut filter.
const documents = () => [
  {
    id: 'doc-1',
    name: 'General Terms & Conditions',
    source: DOC_SOURCE.PLANNING,
    fileName: 'gtc-crude-2026.pdf',
    fileContentType: 'application/pdf',
    lastUpdated: '2026-07-18T11:30:00+00:00',
  },
  {
    id: 'doc-2',
    name: 'Assay Summary Sheet',
    source: DOC_SOURCE.PLANNING,
    fileName: 'assay-summary.xlsx',
    fileContentType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    lastUpdated: '2026-07-19T08:05:00+00:00',
  },
  {
    id: 'doc-3',
    name: 'Tender Instruction to Bidders',
    source: DOC_SOURCE.PROCUREMENT,
    fileName: 'instruction-to-bidders.pdf',
    fileContentType: 'application/pdf',
    lastUpdated: '2026-07-21T14:00:00+00:00',
  },
  {
    id: 'doc-4',
    name: 'HPS Working Paper',
    source: DOC_SOURCE.PROCUREMENT,
    fileName: 'hps-working-paper.xlsx',
    fileContentType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    lastUpdated: '2026-07-22T09:15:00+00:00',
  },
];

const record = ({
  id,
  procId,
  title,
  description,
  status,
  submissionDeadline,
  validityPeriod,
  invitationDetail,
  participant = null,
  contractType = 'Term',
}) => ({
  id,
  procId,
  title,
  procurementType: 'Crude dan Condensat',
  procurementMethod: 'Pelelangan khusus',
  description,
  invitationStatus: status,
  // Filled when the member confirms as a Delegate ({ mode, name, email }); for a
  // Present confirmation only { mode } is stored; null while still Open.
  participant,
  // Data captured when the "Invitation to Panitia Lelang" was created.
  invitationDetail,
  requirement: {
    contractType,
    startPeriod: '2026-09-01T00:00:00+00:00',
    endPeriod: '2027-08-31T00:00:00+00:00',
    cargos: [cargo()],
    clauses: clauses(),
    additionalNotes:
      'Seller reserves the right to reject any and all offers without stating a reason.',
    validity: { submissionDeadline, validityPeriod },
  },
  supportingDocuments: documents(),
});

const invitationDetail = over => ({
  date: '2026-08-05T00:00:00+07:00',
  timeStart: '09:00',
  timeEnd: '12:00',
  place: 'Ruang Rapat Lantai 5, Kantor Pusat Pertamina, Jakarta',
  agenda: 'Pembukaan penawaran dan verifikasi dokumen peserta lelang.',
  ...over,
});

export const SEED_INVITATIONS = [
  record({
    id: 'invi-1',
    procId: 'CRUDE-20072026-0001',
    title: 'Crude Term Q3 2026 - RU II Dumai',
    description: 'Term procurement of light sweet crude for RU II Dumai CDU.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-12-15T17:00:00+00:00',
    validityPeriod: '2027-12-31T17:00:00+00:00',
    invitationDetail: invitationDetail(),
  }),
  record({
    id: 'invi-2',
    procId: 'CRUDE-21072026-0002',
    title: 'Condensate Spot Cargo - RU III Plaju',
    description: 'Single spot condensate cargo, September laycan.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-11-20T17:00:00+00:00',
    validityPeriod: '2027-12-05T17:00:00+00:00',
    contractType: 'Spot',
    invitationDetail: invitationDetail({
      place: 'Ruang Rapat RU III Plaju, Palembang',
      timeStart: '10:00',
      timeEnd: '13:00',
    }),
  }),
  record({
    id: 'invi-3',
    procId: 'CRUDE-15072026-0003',
    title: 'Crude Term H2 2026 - RU IV Cilacap',
    description: 'Medium sour crude term contract for RU IV Cilacap.',
    status: INVITATION_STATUS.CONFIRMED,
    submissionDeadline: '2026-08-25T17:00:00+00:00',
    validityPeriod: '2026-09-10T17:00:00+00:00',
    invitationDetail: invitationDetail(),
    participant: { mode: 'Present', name: null, email: null },
  }),
  record({
    id: 'invi-4',
    procId: 'CRUDE-14072026-0004',
    title: 'Crude Spot Cargo - RU V Balikpapan',
    description: 'Heavy crude spot cargo for RU V Balikpapan.',
    status: INVITATION_STATUS.CONFIRMED,
    submissionDeadline: '2026-08-24T17:00:00+00:00',
    validityPeriod: '2026-09-08T17:00:00+00:00',
    contractType: 'Spot',
    invitationDetail: invitationDetail(),
    participant: {
      mode: 'Delegate',
      name: 'Budi Santoso',
      email: 'budi.santoso@pertamina.com',
    },
  }),
  record({
    id: 'invi-5',
    procId: 'CRUDE-01062026-0005',
    title: 'Crude Term Q2 2026 - RU VI Balongan',
    description: 'Light sweet crude term contract for RU VI Balongan.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-10-10T17:00:00+00:00',
    validityPeriod: '2027-10-25T17:00:00+00:00',
    invitationDetail: invitationDetail(),
  }),
  record({
    id: 'invi-6',
    procId: 'CRUDE-22072026-0006',
    title: 'Condensate Term 2027 - RU III Plaju',
    description: 'Annual condensate term supply for RU III Plaju.',
    status: INVITATION_STATUS.CONFIRMED,
    submissionDeadline: '2026-08-26T17:00:00+00:00',
    validityPeriod: '2026-09-12T17:00:00+00:00',
    invitationDetail: invitationDetail(),
    participant: {
      mode: 'Delegate',
      name: 'Siti Rahma',
      email: 'siti.rahma@pertamina.com',
    },
  }),
  record({
    id: 'invi-7',
    procId: 'CRUDE-19072026-0007',
    title: 'Crude Spot Cargo - RU II Dumai',
    description: 'Opportunistic light crude spot purchase.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-09-23T17:00:00+00:00',
    validityPeriod: '2027-10-06T17:00:00+00:00',
    contractType: 'Spot',
    invitationDetail: invitationDetail(),
  }),
  record({
    id: 'invi-8',
    procId: 'CRUDE-10052026-0008',
    title: 'Crude Term Q1 2026 - RU IV Cilacap',
    description: 'Term crude contract for RU IV Cilacap.',
    status: INVITATION_STATUS.CONFIRMED,
    submissionDeadline: '2026-08-20T17:00:00+00:00',
    validityPeriod: '2026-09-04T17:00:00+00:00',
    invitationDetail: invitationDetail(),
    participant: { mode: 'Present', name: null, email: null },
  }),
  record({
    id: 'invi-9',
    procId: 'CRUDE-23072026-0009',
    title: 'Crude Term Q4 2026 - RU V Balikpapan',
    description: 'Medium crude term contract covering Q4 2026.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-10-30T17:00:00+00:00',
    validityPeriod: '2027-11-15T17:00:00+00:00',
    invitationDetail: invitationDetail(),
  }),
  record({
    id: 'invi-10',
    procId: 'CRUDE-23072026-0010',
    title: 'Condensate Spot Cargo - RU VI Balongan',
    description: 'Prompt condensate cargo, October delivery.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-10-05T17:00:00+00:00',
    validityPeriod: '2027-10-20T17:00:00+00:00',
    contractType: 'Spot',
    invitationDetail: invitationDetail(),
  }),
  record({
    id: 'invi-11',
    procId: 'CRUDE-18072026-0011',
    title: 'Crude Term 2027 - RU II Dumai',
    description: 'Twelfth record — exists so pagination has a second page.',
    status: INVITATION_STATUS.CONFIRMED,
    submissionDeadline: '2026-08-27T17:00:00+00:00',
    validityPeriod: '2026-09-14T17:00:00+00:00',
    invitationDetail: invitationDetail(),
    participant: { mode: 'Present', name: null, email: null },
  }),
  record({
    id: 'invi-12',
    procId: 'CRUDE-17072026-0012',
    title: 'Crude Spot Cargo - RU III Plaju',
    description: 'Spot cargo invitation for the committee session.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-09-22T17:00:00+00:00',
    validityPeriod: '2027-10-05T17:00:00+00:00',
    contractType: 'Spot',
    invitationDetail: invitationDetail(),
  }),
];
